// Section 22 People tab, left panel and jobs board: rows for everyone with the draft's pending
// changes, sorting, grouping above 60 people (Section 3), and the left panel's filters.
import {
  catalog,
  type Decision,
  type GameState,
  type HouseholdId,
  type JobId,
  type PersonId,
  type SkillId,
} from '../sim/api';
import { memo } from './memo';

/** Section 22 status glyphs (hungry, sick, injured, grieving, idle, in school, pregnant, in custody). */
export type PersonFlag =
  | 'sick'
  | 'injured'
  | 'pregnant'
  | 'grieving'
  | 'hungry'
  | 'idle'
  | 'in-school'
  | 'in-custody'
  | 'stunted'
  | 'child'
  | 'elder';

/** Flags shown as glyphs, in display order (age groups are a column, not a glyph). */
export const GLYPH_FLAGS: readonly PersonFlag[] = [
  'hungry',
  'sick',
  'injured',
  'grieving',
  'pregnant',
  'idle',
  'in-school',
  'in-custody',
  'stunted',
];

export interface PersonRow {
  id: PersonId;
  name: string;
  /** Pending rename in the draft, if any. */
  pendingName: string | null;
  sex: 'female' | 'male';
  ageYears: number;
  ageGroup: 'child' | 'adult' | 'elder';
  job: JobId;
  jobLabel: string;
  /** True when the draft changes this person's job. */
  pending: boolean;
  strength: number;
  health: number;
  happiness: number;
  trust: number;
  /** Skill in the (drafted) job, 0..5. */
  skill: number;
  topSkill: { job: JobId; level: number } | null;
  /** Up to three best skills, highest first. */
  topSkills: { skill: SkillId; level: number }[];
  household: HouseholdId;
  /** The household's eldest adult member, to name it ("Ada's household"). */
  householdName: string;
  flags: PersonFlag[];
}

export function jobLabel(job: JobId): string {
  return catalog().jobs.find((j) => j.id === job)?.label ?? job;
}

function householdNames(game: GameState): Map<HouseholdId, string> {
  const names = new Map<HouseholdId, string>();
  for (const h of Object.values(game.households)) {
    let head: { name: string; age: number } | null = null;
    for (const id of h.memberIds) {
      const p = game.people[id];
      if (p !== undefined && (head === null || p.ageSeasons > head.age))
        head = { name: p.name, age: p.ageSeasons };
    }
    names.set(h.id, head?.name ?? h.id);
  }
  return names;
}

export const peopleRows = memo((game: GameState, draft: Decision | null): PersonRow[] => {
  const { seasonsPerYear: perYear, adultAtSeasons, elderAtSeasons, shortThreshold } = catalog();
  const last = game.history[game.history.length - 1];
  const households = householdNames(game);
  // D-43, P-03: retirees at rest are not idle (no idle happiness or trust penalty).
  const retireAge = game.policies.retirementAge;
  const retiredAt = retireAge === 'never' ? Number.POSITIVE_INFINITY : retireAge * perYear;
  return Object.keys(game.people)
    .sort()
    .map((key) => {
      const p = game.people[key as PersonId];
      if (p === undefined) throw new Error(`missing person ${key}`);
      const drafted = draft?.assignments[p.id];
      const job = drafted ?? p.job;
      const ageGroup =
        p.ageSeasons < adultAtSeasons ? 'child' : p.ageSeasons >= elderAtSeasons ? 'elder' : 'adult';
      const flags: PersonFlag[] = [];
      if ((last?.needs[p.id]?.food ?? 1) < shortThreshold) flags.push('hungry');
      if (p.status.sick !== null) flags.push('sick');
      if (p.status.injured !== null) flags.push('injured');
      if (p.status.grieving !== null) flags.push('grieving');
      if (p.status.pregnant !== null) flags.push('pregnant');
      if (job === 'rest' && ageGroup !== 'child' && p.ageSeasons < retiredAt) flags.push('idle');
      if (p.status.inSchool) flags.push('in-school');
      if (p.status.inCustody !== null) flags.push('in-custody');
      if (p.status.stunted) flags.push('stunted');
      if (ageGroup !== 'adult') flags.push(ageGroup);
      const skills = (Object.entries(p.skills) as [SkillId, number][])
        .filter(([, level]) => level > 0)
        .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
      const top = skills[0];
      const rename = draft?.renames[p.id];
      return {
        id: p.id,
        name: p.name,
        pendingName: rename === undefined || rename === p.name ? null : rename,
        sex: p.sex,
        ageYears: Math.floor(p.ageSeasons / perYear),
        ageGroup,
        job,
        jobLabel: jobLabel(job),
        pending: drafted !== undefined && drafted !== p.job,
        strength: p.strength,
        health: p.health,
        happiness: p.happiness,
        trust: p.trust,
        skill: job === 'rest' ? 0 : (p.skills[job] ?? 0),
        topSkill: top === undefined ? null : { job: top[0], level: top[1] },
        topSkills: skills.slice(0, 3).map(([skill, level]) => ({ skill, level })),
        household: p.householdId,
        householdName: households.get(p.householdId) ?? p.householdId,
        flags,
      };
    });
});

// ---- Sorting and grouping (People tab).

export type SortKey =
  | 'name'
  | 'age'
  | 'sex'
  | 'job'
  | 'strength'
  | 'health'
  | 'happiness'
  | 'trust'
  | 'skill'
  | 'household';

const compareBy: Record<SortKey, (a: PersonRow, b: PersonRow) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  age: (a, b) => a.ageYears - b.ageYears,
  sex: (a, b) => a.sex.localeCompare(b.sex),
  job: (a, b) => a.jobLabel.localeCompare(b.jobLabel),
  strength: (a, b) => a.strength - b.strength,
  health: (a, b) => a.health - b.health,
  happiness: (a, b) => a.happiness - b.happiness,
  trust: (a, b) => a.trust - b.trust,
  skill: (a, b) => (a.topSkill?.level ?? 0) - (b.topSkill?.level ?? 0),
  household: (a, b) => a.householdName.localeCompare(b.householdName),
};

export function sortRows(rows: readonly PersonRow[], key: SortKey, dir: 'asc' | 'desc'): PersonRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => sign * compareBy[key](a, b) || (a.id < b.id ? -1 : 1));
}

/** Section 3 and 22: per-person views switch to grouped views above this many people. */
export const GROUP_ABOVE = 60;

export interface PeopleGroup {
  key: string;
  kind: 'household' | 'job';
  /** The household's name or the job's label. */
  label: string;
  rows: PersonRow[];
  expanded: boolean;
  /** Members with any attention flag (hungry, sick, injured). */
  flagged: number;
  meanStrength: number;
}

export interface PeopleTableVM {
  total: number;
  groupBy: 'none' | 'household' | 'job';
  rows: PersonRow[];
  groups: PeopleGroup[];
}

const ATTENTION: readonly PersonFlag[] = ['hungry', 'sick', 'injured'];

export const peopleTableVM = memo(
  (
    game: GameState,
    draft: Decision | null,
    sortKey: SortKey,
    sortDir: 'asc' | 'desc',
    grouping: 'auto' | 'none' | 'household' | 'job',
    expanded: readonly string[],
  ): PeopleTableVM => {
    const rows = sortRows(peopleRows(game, draft), sortKey, sortDir);
    const groupBy =
      grouping === 'auto' ? (rows.length > GROUP_ABOVE ? 'household' : 'none') : grouping;
    if (groupBy === 'none') return { total: rows.length, groupBy, rows, groups: [] };
    const byKey = new Map<string, PersonRow[]>();
    for (const r of rows) {
      const key = groupBy === 'household' ? r.household : r.job;
      const list = byKey.get(key);
      if (list === undefined) byKey.set(key, [r]);
      else list.push(r);
    }
    const groups = [...byKey.entries()].map(([key, members]): PeopleGroup => {
      const first = members[0];
      return {
        key,
        kind: groupBy,
        label: groupBy === 'household' ? (first?.householdName ?? key) : (first?.jobLabel ?? key),
        rows: members,
        expanded: expanded.includes(key),
        flagged: members.filter((m) => m.flags.some((f) => ATTENTION.includes(f))).length,
        meanStrength: members.reduce((s, m) => s + m.strength, 0) / Math.max(1, members.length),
      };
    });
    groups.sort((a, b) => a.label.localeCompare(b.label) || (a.key < b.key ? -1 : 1));
    return { total: rows.length, groupBy, rows, groups };
  },
);

// ---- Left panel: compact list with filters.

export type PeopleFilter =
  | { kind: 'all' }
  | { kind: 'household'; householdId: HouseholdId }
  | { kind: 'job'; jobId: JobId };

export interface PeopleListVM {
  rows: PersonRow[];
  total: number;
  households: { id: HouseholdId; name: string; size: number }[];
  jobs: { id: JobId; label: string; count: number }[];
}

export const peopleListVM = memo(
  (game: GameState, draft: Decision | null, filter: PeopleFilter): PeopleListVM => {
    const all = sortRows(peopleRows(game, draft), 'name', 'asc');
    const rows = all.filter((r) =>
      filter.kind === 'all'
        ? true
        : filter.kind === 'household'
          ? r.household === filter.householdId
          : r.job === filter.jobId,
    );
    const households = new Map<HouseholdId, { id: HouseholdId; name: string; size: number }>();
    const jobs = new Map<JobId, { id: JobId; label: string; count: number }>();
    for (const r of all) {
      const h = households.get(r.household);
      if (h === undefined) households.set(r.household, { id: r.household, name: r.householdName, size: 1 });
      else h.size++;
      const j = jobs.get(r.job);
      if (j === undefined) jobs.set(r.job, { id: r.job, label: r.jobLabel, count: 1 });
      else j.count++;
    }
    return {
      rows,
      total: all.length,
      households: [...households.values()].sort((a, b) => a.name.localeCompare(b.name)),
      jobs: [...jobs.values()].sort((a, b) => a.label.localeCompare(b.label)),
    };
  },
);

// ---- Jobs board columns (kept for callers of the M1 board).

export interface JobColumn {
  id: JobId;
  label: string;
  heavy: boolean;
  people: PersonRow[];
}

export function jobsBoard(game: GameState, draft: Decision | null): JobColumn[] {
  const rows = peopleRows(game, draft);
  return catalog().jobs.map((j) => ({
    id: j.id,
    label: j.label,
    heavy: j.heavy,
    people: rows.filter((r) => r.job === j.id),
  }));
}
